import type React from 'react';
import type { RiskAssessmentConfig } from '@safetyhub/shared';
import { scoreToRiskLevel, severityToConfigLabel, getRiskColor } from '@safetyhub/shared';
import { hexToInlineStyle } from '../../../lib/risk-color-utils';

type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'GOOD';

interface SeverityBadgeProps {
  /** 기존 severity enum (호환성, deprecated) */
  severity?: Severity;
  /** Phase N3: 발생 가능성 점수 (1~10) */
  likelihoodScore?: number | null;
  /** Phase N3: 피해 강도 점수 (1~10) */
  severityScore?: number | null;
  /** Phase 4: AI 산출 위험도 score (1~10). THREE_LEVEL config에서 우선 사용 */
  riskLevelScore?: number | null;
  /** Phase N3: RiskAssessmentConfig (config 기반 라벨 산출) */
  config?: RiskAssessmentConfig | null;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const SEVERITY_STATIC: Record<Severity, { label: string; bg: string; text: string; border: string }> = {
  LOW: {
    label: '낮음',
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    border: 'border-gray-300',
  },
  MEDIUM: {
    label: '보통',
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    border: 'border-yellow-300',
  },
  HIGH: {
    label: '높음',
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-300',
  },
  CRITICAL: {
    label: '심각',
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-300',
  },
  GOOD: {
    label: '우수',
    bg: 'bg-emerald-100',
    text: 'text-emerald-800',
    border: 'border-emerald-300',
  },
};

const sizeClasses = {
  sm: 'px-1.5 py-0.5 text-xs',
  md: 'px-2 py-1 text-sm',
  lg: 'px-3 py-1.5 text-base',
};

const HEX_TO_NAME: Record<string, string> = {
  '#ef4444': 'red',
  '#dc2626': 'red',
  '#f97316': 'orange',
  '#ea580c': 'orange',
  '#f59e0b': 'orange',
  '#eab308': 'yellow',
  '#facc15': 'yellow',
  '#22c55e': 'green',
  '#16a34a': 'green',
  '#10b981': 'emerald',
  '#3b82f6': 'blue',
};

/** color 문자열을 Tailwind 배경/텍스트/테두리 클래스로 변환. 미매칭 hex는 inline style fallback */
function colorToClasses(color: string): { bg: string; text: string; border: string; style?: React.CSSProperties } {
  const map: Record<string, { bg: string; text: string; border: string }> = {
    red: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' },
    yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
    green: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
    blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
    gray: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
  };
  const lower = color?.toLowerCase();
  const normalized = HEX_TO_NAME[lower] ?? color;
  if (map[normalized]) return map[normalized];

  // 사전 정의 미매칭 hex: inline style fallback
  if (/^#[0-9a-f]{6}$/i.test(lower)) {
    const inlineStyle = hexToInlineStyle(lower);
    if (inlineStyle) return { bg: '', text: '', border: '', style: inlineStyle };
  }

  return map['gray'];
}

export function SeverityBadge({
  severity,
  likelihoodScore,
  severityScore,
  riskLevelScore,
  config,
  size = 'sm',
  showLabel = true,
}: SeverityBadgeProps) {
  let label: string;
  let colorClasses: { bg: string; text: string; border: string; style?: React.CSSProperties };

  const hasScore =
    likelihoodScore != null && severityScore != null && config != null;

  if (hasScore) {
    const result = scoreToRiskLevel(likelihoodScore!, severityScore!, config!, riskLevelScore ?? undefined);
    if (result) {
      label = result.riskLabel || '알 수 없음';
      const color = result.color || getRiskColor(result.riskLabel, config!);
      colorClasses = colorToClasses(color);
    } else {
      label = '알 수 없음';
      colorClasses = colorToClasses('gray');
    }
  } else if (severity === 'GOOD') {
    label = '우수';
    colorClasses = colorToClasses('emerald');
  } else if (severity && config) {
    const configLabel = severityToConfigLabel(severity, config);
    label = configLabel ?? SEVERITY_STATIC[severity]?.label ?? severity;
    const color = getRiskColor(label, config);
    colorClasses = colorToClasses(color);
  } else if (severity) {
    const staticConfig = SEVERITY_STATIC[severity];
    label = staticConfig?.label ?? severity;
    colorClasses = { bg: staticConfig?.bg ?? 'bg-gray-100', text: staticConfig?.text ?? 'text-gray-700', border: staticConfig?.border ?? 'border-gray-300' };
  } else {
    label = '알 수 없음';
    colorClasses = colorToClasses('gray');
  }

  return (
    <span
      className={`
        inline-flex items-center font-medium rounded border
        ${colorClasses.bg} ${colorClasses.text} ${colorClasses.border}
        ${sizeClasses[size]}
      `}
      style={colorClasses.style}
    >
      {showLabel ? label : (severity ?? label)}
    </span>
  );
}

/**
 * 위험도 아이콘 (색상만 표시)
 */
export function SeverityDot({ severity, size = 'md' }: { severity: Severity; size?: 'sm' | 'md' | 'lg' }) {
  const dotSizes = {
    sm: 'w-2 h-2',
    md: 'w-3 h-3',
    lg: 'w-4 h-4',
  };

  const dotColors: Record<Severity, string> = {
    LOW: 'bg-gray-400',
    MEDIUM: 'bg-yellow-500',
    HIGH: 'bg-orange-500',
    CRITICAL: 'bg-red-500',
    GOOD: 'bg-emerald-500',
  };

  return (
    <span
      className={`inline-block rounded-full ${dotSizes[size]} ${dotColors[severity]}`}
      title={SEVERITY_STATIC[severity].label}
    />
  );
}

export default SeverityBadge;
